/*
 * Code-of-Conduct dialog with agreement and sign-out actions.
 * AccountLayers mounts it for the consent service used by startup and expired sessions.
 * Keeps the document visible while the accepted account change finishes.
 * Example: registerComponents(registry);
 *          <ConsentLayer context={context} controller={consent}/>
 */

import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { servicesContext } from '../../runtime/services.js';
import { streamSession } from '../../streaming/stream-session.js';
import { runSignOut } from './sign-out.js';
import { createConsent } from './coc-modal.js';
import {
  registerComponents as registerAuthored,
  AuthoredContent
} from '../editing/authored-content.jsx';

export class ConsentSession extends UIElement {
  static properties = {
    request: { attribute: false },
    error: { state: true },
    signingOut: { state: true }
  };

  constructor() {
    super();
    this.error = '';
    this.signingOut = false;
    this.captureOverlay = (node) => (this.overlay = node);
    this.captureCard = (node) => (this.card = node);
    this.captureAgree = (node) => (this.agreeNode = node);
  }

  updated(changed) {
    super.updated(changed);
    const life = this.connection;
    if (!life || !this.card || this.boundConnection === life) return;
    this.boundConnection = life;
    servicesContext(this.context, ['a11y', 'api', 'auth']);
    const request = this.request;
    const modal = this.context.services.a11y.openModal({
      overlay: this.overlay,
      card: this.card,
      initialFocus: this.agreeNode,
      closeOnOverlayClick: false,
      closeOnEscape: false,
      signal: life.signal,
      onClose: () => request.close()
    });
    this.modal = modal;
    life.own(() => modal.destroy());
    life.own(
      this.context.services.auth.subscribeAuth(
        life.guard(() => {
          const user = this.context.services.auth.getUser();
          if (!user || user.id !== request.ownerId) request.close();
        })
      )
    );
  }

  async agree() {
    const { auth, api } = this.context.services;
    const session = streamSession(auth.getToken()),
      request = this.request;
    this.error = '';
    try {
      const updated = await api.post('/api/me/accept-code-of-conduct', {
        version: request.doc.version
      });
      if (session !== streamSession(auth.getToken())) {
        request.close();
        return;
      }
      auth.updateUser({ accepted_coc_version: updated.accepted_coc_version });
      if (!this.disposed) this.context.window.location.reload();
    } catch (error) {
      if (!this.disposed)
        this.error = error.message || 'Could not record your agreement. Try again.';
    }
  }

  signOut() {
    const request = this.request,
      context = this.context;
    this.signingOut = true;
    return runSignOut({
      context,
      onStart: () => {
        if (!this.disposed) this.error = '';
      },
      onSuccess() {
        request.close();
        context.window.location.assign('/');
      },
      onExpired: () => request.close(),
      onError: (error) => {
        if (!this.disposed) this.error = error.message;
      }
    }).finally(() => {
      if (!this.disposed) this.signingOut = false;
    });
  }

  closeNative() {
    this.modal?.close();
  }

  render() {
    const doc = this.request?.doc;
    if (!doc) return null;
    return this.portal(
      'consent',
      'body',
      <div ref={this.captureOverlay} class="modal-overlay coc-overlay">
        <div ref={this.captureCard} class="modal-card coc-card" role="dialog" aria-modal="true" aria-labelledby="coc-modal-title" tabindex="-1">
          <h2 id="coc-modal-title" class="modal-title">{doc.title || 'Code of Conduct'}</h2>
          <AuthoredContent
            class="prose-post coc-body"
            value={doc.body || ''}
            reportImages={false}/>
          <div class={`alert-error coc-error${this.error ? '' : ' hidden'}`}>{this.error}</div>
          <div class="modal-actions" commandScope>
            <button type="button" class="button-secondary" command={this.signOut}>
              {this.signingOut ? 'Signing out…' : 'Sign out'}
            </button>
            <button
              ref={this.captureAgree}
              type="button"
              class="button-primary"
              command={this.agree}>
              I agree
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export class ConsentLayer extends UIElement {
  static properties = { controller: { attribute: false }, request: { state: true } };

  constructor() {
    super();
    this.request = null;
    this.capture = (node) => (this.session = node);
    this.bindMethods('present');
  }

  updated(changed) {
    super.updated(changed);
    const life = this.connection;
    if (!life) return;
    const controller = this.controller || this.context.service(createConsent);
    if (this.boundConnection !== life || this.boundController !== controller) {
      this.releasePresenter?.();
      this.boundConnection = life;
      this.boundController = controller;
      const release = controller.attach(this.present);
      this.releasePresenter = release;
      life.own(() => {
        release();
        if (this.releasePresenter === release) this.releasePresenter = null;
      });
    }
  }

  present(request) {
    const session = this.session;
    this.request = request;
    if (!request && session) {
      try {
        session.closeNative();
      } finally {
        session.dispose();
      }
    }
    return request ? this.whenRendered() : undefined;
  }

  render() {
    return (
      <>
        {this.request
          ? [<ConsentSession key={this.request} ref={this.capture} request={this.request}/>]
          : null}
      </>
    );
  }
}

export function registerComponents(registry) {
  registerAuthored(registry);
  defineUIElements(registry, {
    'tbc-consent-session': ConsentSession,
    'tbc-consent-layer': ConsentLayer
  });
}
